// @ts-check
import { f7 } from 'framework7-vue';

/**
 * @typedef {object} ShowToastParameters
 * @property {string} [icon] - Toast icon HTML layout, e.g. <i class="fa fa-info"></i> or image <img src="path/to/icon.png">
 * @property {string} [title] - Toast inner title. When it's not empty, a header is going to be added
 * @property {string} [text] - Toast inner text
 * @property {('top' | 'center' | 'bottom')} [position] - Toast position. Can be bottom, center or top
 * @property {('left' | 'center' | 'right')} [horizontalPosition] - Toast horizontal alignment on wide screen. Has effects only on top and bottom toasts.
 * @property {('primary'|'secondary'|'success'|'danger'|'warning'|'info'|'light'|'dark')} [type] - Toast type
 * @property {boolean} [typeIcon=false] - Toast type icon
 * @property {boolean} [closeButton=false] - Adds toast close button
 * @property {boolean} [elapsedTime=false] - Adds toast elapsed time text
 * @property {number} [closeTimeout=2000] - Timeout delay (in ms) to close toast automatically
 * @property {string} [cssClass] - Additional css class to add
 * @property {boolean} [destroyOnClose=false] - Destroys toast instance on close
 */

// A constant object that contains the HTML icons for each toast type
const typeIcons = Object.freeze({
  primary: '',
  secondary: '',
  success: '<i class="fa fa-check-circle"></i>',
  danger: '<i class="fa fa-exclamation-triangle"></i>',
  warning: '<i class="fa fa-exclamation-triangle"></i>',
  info: '<i class="fa fa-info-circle"></i>',
  light: '',
  dark: '',
});

// Constants for toast position
const toastVPos = Object.freeze({
  TOP: 'top',
  CENTER: 'center',
  BOTTOM: 'bottom',
});

// Constants for toast horizontal position
const toastHPos = Object.freeze({
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
});

// Constants for different toast types
const toastType = Object.freeze({
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  SUCCESS: 'success',
  DANGER: 'danger',
  WARNING: 'warning',
  INFO: 'info',
  LIGHT: 'light',
  DARK: 'dark',
});

// Time interval (in milliseconds) to update the toast elapsed time (< 60000)
const UPDATE_MS = 5000;

/**
 * Returns a string representing the time elapsed since the given timestamp
 * @param {number} ms - The elapsed time in milliseconds
 * @returns {string} The formatted time ago string
 */
function timeAgo(ms) {
  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;

  if (ms < UPDATE_MS) {
    return 'ahora';
  } if (ms < MINUTE) {
    const seconds = Math.floor(ms / 1000);
    return `hace ${seconds} seg.`;
  } if (ms < HOUR) {
    const minutes = Math.floor(ms / MINUTE);
    return `hace ${minutes} min.`;
  }
  const hours = Math.floor(ms / HOUR);
  return `hace ${hours} h.`;
}

/**
 * Updates the elapsed time element of the given toast
 * @param {ReturnType<typeof f7.toast.show>} toast - The toast object
 * @param {number} startTime - The timestamp when the toast was created
 */
function updateElapsedTime(toast, startTime) {
  const elapsedTimeDiv = toast.el.querySelector('.toast-elapsed-time');
  if (elapsedTimeDiv) {
    const elapsed = Date.now() - startTime;
    elapsedTimeDiv.textContent = timeAgo(elapsed);
  }
}

/**
 * @param {ShowToastParameters} parameters
 * @returns {void}
 */
export function showToast(parameters) {
  const {
    icon,
    title,
    text,
    position = toastVPos.BOTTOM,
    horizontalPosition = toastHPos.LEFT,
    type = toastType.PRIMARY,
    typeIcon = false,
    closeButton = false,
    elapsedTime = false,
    closeTimeout = 2000,
    cssClass,
    destroyOnClose = false,
  } = parameters;

  // Get the time when the toast was created
  const startTime = new Date().getTime();

  // Create the toast using Framework7
  const toast = f7.toast.create({
    closeButton,
    closeTimeout,
    destroyOnClose,
    // Custom function to render Toast. Must return toast html
    render() {
      let iconDiv = '';
      if (icon) {
        iconDiv = `<div class="toast-icon">${icon}</div>`;
      } else if (typeIcon && typeIcons[type]) {
        iconDiv = `<div class="toast-icon">${typeIcons[type]}</div>`;
      }
      const textElement = text ? `<div class="toast-text">${text}</div>` : '';
      const closeBtnElement = closeButton ? '<a class="toast-button button"></a>' : '';
      const elapsedTimeElement = elapsedTime ? '<small class="toast-elapsed-time">ahora</small>' : '';
      const horizontalClass = position === toastVPos.TOP || position === toastVPos.BOTTOM
        ? `toast-horizontal-${horizontalPosition}`
        : '';

      let html = `
          <div
            class="toast toast-${position} ${horizontalClass}
            alert-hg alert-hg-${type}
            ${cssClass || ''}"
          >
        `;
      if (title) {
        html += `
            <div class="toast-header">
              ${iconDiv}
              <strong class="toast-title">${title}</strong>
              ${elapsedTimeElement}
              ${closeBtnElement}
            </div>
            <div class="toast-content">
              ${textElement}
            </div>
          `;
      } else {
        html += `
            <div class="toast-content">
              ${iconDiv}
              ${textElement}
              ${elapsedTimeElement}
              ${closeBtnElement}
            </div>
          `;
      }
      html += '</div>';
      return html;
    },
  });

  if (elapsedTime) {
    const intervalId = setInterval(() => {
      updateElapsedTime(toast, startTime);
    }, UPDATE_MS);

    toast.on('close', () => {
      clearInterval(intervalId);
    });
  }

  toast.open();
}
